import { memo, useCallback, useMemo } from "react";
import { Btn, P } from "../../../../../AbstractElements";
import CustomDataTable from "../../../../../CommonElements/Table/CustomTable";
import SkeletonTable from "../../../../../CommonElements/Loader/SkeletonTableLoader";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { NA, primary } from "../../../../../utils/Constant";
import {
  IAccountConveyanceReportResposne,
  ITeamMembersConveyanceList,
} from "../../../../../Types/TeamMembers";
import { ITeamMembersConveyanceTableProps } from "../../types";
import { TableWrapper } from "./ConveyanceTable.style";
import { useConveyanceEditing } from "../../hooks/useConveyanceEditing";
import ApproveAmountCell from "../Cells/ApprovalAmountCell";
import { TableColumn } from "react-data-table-component";
import BadgeDetails from "../../../../../CommonElements/Badge/BadgeDetails";
import { Link } from "react-router-dom";

const extractNumericId = (code: string) =>
  Number(code?.replace(/\D/g, "") || 0);

const normalizeApprovalRole = (role?: string | null) =>
  String(role ?? "")
    .trim()
    .toLowerCase()
    .replace(/s$/, "");

const isApprovedByRole = (
  row: ITeamMembersConveyanceList,
  approvalRoles: string[],
) =>
  (row.last_approval ?? []).some(
    (approval) =>
      approval.status?.toLowerCase() === "approved" &&
      approvalRoles.includes(normalizeApprovalRole(approval.by)),
  );

const isApprovalLockedForRole = (
  row: ITeamMembersConveyanceList,
  loginEmployeeRole: string,
) => {
  const role = normalizeApprovalRole(loginEmployeeRole);

  if (role === "manager") {
    return isApprovedByRole(row, ["hr", "account"]);
  }

  if (role === "hr") {
    return isApprovedByRole(row, ["account"]);
  }

  return false;
};

const TeamMembersConveyanceTable = ({
  data,
  pageSize,
  onPageChange,
  onRowsPageChange,
  loginEmployeeRole,
  openConveyanceDetails,
  conveyanceReportLoading,
  openConveyanceDialog,
  onRowSelected,
  conveyanceStatus,
  isClearSelectedRows,
  selectedRowKeys,
  setSelectedRowKeys,
  filterStatus,
  useManagerColumns = false,
  useAccountColumns = false,
  hideActionButtons = false,
  disableSelection = false,
  detailsBasePath = "/account-zone/conveyance",
}: ITeamMembersConveyanceTableProps) => {
  const isManager = loginEmployeeRole === "manager";
  const isAccount = loginEmployeeRole === "account";
  const shouldUseManagerColumns = isManager || useManagerColumns;
  const shouldUseAccountColumns =
    (isAccount && !useManagerColumns) || useAccountColumns;

  /* ---------- ORIGINAL DATA (Never Mutated) ---------- */
  const originalRows = useMemo(() => data?.data ?? [], [data]);

  /* Typed versions */
  const originalAccountRows =
    originalRows as IAccountConveyanceReportResposne[];
  const normalizedManagerRows = useMemo<ITeamMembersConveyanceList[]>(() => {
    if (!useManagerColumns) return originalRows as ITeamMembersConveyanceList[];

    if (!originalRows.length) return [];

    if ("conveyance_code" in originalRows[0]) {
      return originalRows as ITeamMembersConveyanceList[];
    }

    return (originalRows as IAccountConveyanceReportResposne[]).flatMap(
      (accountRow) =>
        (accountRow.details ?? []).map((detail) => ({
          amount: detail.amount ?? "",
          approval_status: detail.status ?? "",
          approved_amount: detail.approved_amount ?? "",
          bill: detail.bill ?? null,
          calculated_amount: detail.calculated_amount ?? null,
          conveyance_code: detail.conveyance_code ?? "",
          conveyance_date: detail.date ?? "",
          distance: detail.distance ?? "",
          last_approval: detail.last_approval ?? [],
          mode: detail.mode ?? "",
          reason: detail.reason ?? "",
          remark: "",
          submitted_on: detail.submitted_on ?? "",
          travel_to: detail.to ?? "",
          travelfrom: detail.from ?? "",
          restriction: accountRow.restriction,
          status: detail.status ?? accountRow.status,
          team_member: {
            id: accountRow.employee_id ?? "",
            name: accountRow.employee_name ?? "",
            email: accountRow.email ?? "",
            department: accountRow.department ?? "",
            designation: accountRow.designation ?? "",
            location: accountRow.location ?? "",
          },
        })),
    );
  }, [originalRows, useManagerColumns]);

  /* ---------- FILTERED + UI Data ---------- */
  const tableData = useMemo(() => {
    let rows = shouldUseManagerColumns
      ? [...normalizedManagerRows]
      : [...originalRows];
    if (isManager && filterStatus === "pending") {
      rows = normalizedManagerRows.filter((d) => !d.restriction);
    }

    return rows.map((row, i) => ({
      ...row,
      _rowClass: `row-animate row-delay-${i}`,
    }));
  }, [
    originalRows,
    normalizedManagerRows,
    filterStatus,
    isManager,
    shouldUseManagerColumns,
  ]);

  /* ---------- Unique Stable Key ---------- */
  const rowKeyOf = useCallback(
    (row: any) =>
      String(
        row.conveyance_code ??
          row.team_member?.id ??
          row.employee_id ??
          Math.random(),
      ),
    [],
  );

  /* ---------- Editing Logic Only for Manager ---------- */
  const {
    patches,
    drafts,
    errors,
    setDraftAndValidate,
    commitEdit,
    getApprovedValue,
  } = useConveyanceEditing(rowKeyOf);

  const handleCommitEdit = useCallback(
    (row: ITeamMembersConveyanceList, value: string) => {
      if (!isManager) return;
      commitEdit(row, value);

      const key = rowKeyOf(row);

      if (selectedRowKeys.includes(key)) {
        const updatedRows = (tableData as ITeamMembersConveyanceList[]).filter(
          (r) => selectedRowKeys.includes(rowKeyOf(r)),
        );

        const enrichedRows = updatedRows.map((r) => ({
          ...r,
          approved_amount: getApprovedValue(r),
        }));

        onRowSelected({
          allSelected: selectedRowKeys.length === tableData.length,
          selectedCount: enrichedRows.length,
          selectedRows: enrichedRows,
        });
      }
    },
    [
      commitEdit,
      isManager,
      selectedRowKeys,
      tableData,
      getApprovedValue,
      onRowSelected,
      rowKeyOf,
    ],
  );

  /* ---------- MANAGER COLUMNS ---------- */
  const managerColumns: TableColumn<ITeamMembersConveyanceList>[] = useMemo(
    () => [
      {
        name: "#",
        width: "40px",
        cell: (_row, index) => <P>{index + 1}</P>,
      },
      {
        name: "Emp Name",
        width: "200px",
        cell: (row) => (
          <div>
            <P className="m-0 f-12">
              {row?.team_member?.id} - {row?.team_member?.name}
            </P>
            <P className="m-0 f-12">{row?.team_member?.email}</P>
          </div>
        ),
      },
      {
        name: "Conv. Date",
        width: "130px",
        cell: (row) => (
          <P className="m-0 f-12">{row?.conveyance_date?.slice(0, 14) ?? NA}</P>
        ),
      },
      {
        name: "Subm. Date",
        width: "130px",
        cell: (row) => (
          <P className="m-0 f-12">{row?.submitted_on?.slice(0, 14) ?? NA}</P>
        ),
      },
      {
        name: "Reason",
        width: "120px",
        cell: (row) => <P className="m-0 f-12">{row?.reason ?? NA}</P>,
      },
      {
        name: "Amount",
        width: "80px",
        cell: (row) => <P className="m-0 f-12">{row?.amount}</P>,
      },
      {
        name: "Appro. Amount",
        width: "168px",
        cell: (row, index) => {
          const key = rowKeyOf(row);
          const tooltipKey = `${key}-${index ?? 0}`;
          const tooltipId = `approval-history-${tooltipKey.replace(
            /[^a-zA-Z0-9_-]/g,
            "-",
          )}`;
          const committed =
            patches[key] ?? Number(row?.approved_amount ?? row?.amount);
          const draft = drafts[key] ?? String(committed);
          const errorMsg = errors[key] ?? null;
          const isApprovalLocked = isApprovalLockedForRole(
            row,
            loginEmployeeRole,
          );

          return (
            <ApproveAmountCell
              row={row}
              keyId={key}
              draft={draft}
              disabled={hideActionButtons || isApprovalLocked}
              errorMsg={errorMsg}
              role={loginEmployeeRole}
              approvalHistory={row?.last_approval ?? []}
              tooltipId={tooltipId}
              onCommit={handleCommitEdit}
              onChange={setDraftAndValidate}
              getApprovedValue={getApprovedValue}
              conveyanceStatus={conveyanceStatus}
              openConveyanceDialog={openConveyanceDialog}
              isShowBtn={selectedRowKeys?.length === 0 && !hideActionButtons}
            />
          );
        },
      },
      {
        name: "Details",
        width: "80px",
        cell: (row) => (
          <IconComponent
            className="icofont icofont-info-circle text-primary f-18 ms-4 pointer"
            onClick={() => openConveyanceDetails(row)}
            title="See More Details"
          />
        ),
      },
    ],
    [
      drafts,
      patches,
      errors,
      handleCommitEdit,
      setDraftAndValidate,
      getApprovedValue,
      conveyanceStatus,
      selectedRowKeys,
      openConveyanceDialog,
      openConveyanceDetails,
      loginEmployeeRole,
      hideActionButtons,
      rowKeyOf,
    ],
  );

  /* ---------- ACCOUNT COLUMNS ---------- */
  const accountColumns: TableColumn<IAccountConveyanceReportResposne>[] =
    useMemo(
      () => [
        {
          name: "#",
          width: "40px",
          cell: (_row, i) => <P className="ps-2">{i + 1}</P>,
        },
        {
          name: "Emp Name",
          width: "200px",
          cell: (row) => (
            <div>
              <P className="m-0 f-12">
                {row?.employee_id} - {row?.employee_name}
              </P>
              <P className="m-0 f-12">{row?.email}</P>
            </div>
          ),
        },
        {
          name: "Center",
          width: "130px",
          cell: (row) => <P className="m-0 f-12">{row?.location ?? NA}</P>,
        },
        {
          name: "Department",
          width: "130px",
          cell: (row) => <P className="m-0 f-12">{row?.department ?? NA}</P>,
        },
        {
          name: "Designation",
          width: "150px",
          cell: (row) => <P className="m-0 f-12">{row?.designation ?? NA}</P>,
        },
        {
          name: "Applied Amt.",
          width: "120px",
          cell: (row) => (
            <P className="m-0 f-12">{row?.total_applied_amount ?? 0}</P>
          ),
        },
        {
          name: "Approved Amt.",
          width: "120px",
          cell: (row) => (
            <P className="m-0 f-12">
              {row?.total_approved_amount ?? row?.total_approved ?? 0}
            </P>
          ),
        },
        {
          name: "Total Amt.",
          width: "130px",
          cell: (row) => (
            <div className="d-flex align-items-center">
              <P className="f-12 my-0 ms-3">{row?.total_amount ?? 0}</P>
              {loginEmployeeRole === "account" &&
                filterStatus === "approved" && (
                  <BadgeDetails
                    color="success"
                    text="Paid"
                    className="text-capitalize badget-success ms-3"
                  />
                )}

              {selectedRowKeys?.length === 0 &&
                loginEmployeeRole === "account" &&
                filterStatus !== "approved" && (
                  <Btn
                    color={primary}
                    className="f-10 btn-pill btn-air-primary btn-xs ms-3"
                    onClick={() => openConveyanceDialog("Approve", row)}
                  >
                    Pay
                  </Btn>
                )}
            </div>
          ),
        },
        {
          name: "Details",
          width: "80px",
          cell: (row) => {
            const firstDetail = row?.details?.[0];

            if (!firstDetail) {
              return <P className="m-0 f-12 text-muted ms-4">{NA}</P>;
            }
           
              

            return (
              <Link
                target="_blank"
                to={`${detailsBasePath}/${extractNumericId(row?.employee_id)}`}
              >
                <IconComponent
                  className="icofont icofont-info-circle text-primary f-18 ms-4 pointer"
                  title="See More Details"
                />
              </Link>
            );
          },
        },
      ],
      [
        filterStatus,
        loginEmployeeRole,
        openConveyanceDetails,
        selectedRowKeys.length,
        detailsBasePath,
      ],
    );

  const columns = shouldUseAccountColumns
    ? (accountColumns as TableColumn<any>[])
    : (managerColumns as TableColumn<any>[]);

  /* ---------- ROW SELECTION (DIFFERENT BEHAVIOR PER ROLE) ---------- */
  const handleSelectRowsChange = (selection: any) => {
    const selectedKeys = selection?.selectedRows?.map((r: any) => rowKeyOf(r));
    setSelectedRowKeys(selectedKeys);

    if (isManager) {
      const enrichedRows = (
        selection?.selectedRows as ITeamMembersConveyanceList[]
      ).map((row) => ({
        ...row,
        approved_amount: getApprovedValue(row),
      }));

      const originalSelected = normalizedManagerRows?.filter((r) =>
        selectedKeys?.includes(rowKeyOf(r)),
      );

      onRowSelected({
        ...selection,
        selectedRows: enrichedRows,
        originalSelectedRows: originalSelected,
      });
    } else {
      const originalSelected = originalAccountRows?.filter((r) =>
        selectedKeys?.includes(rowKeyOf(r)),
      );

      onRowSelected({
        ...selection,
        selectedRows: selection?.selectedRows,
        originalSelectedRows: originalSelected,
      });
    }
  };

  const isRowSelectionDisabled = useCallback(
    (row: any) =>
      shouldUseManagerColumns
        ? hideActionButtons ||
          isApprovalLockedForRole(row as ITeamMembersConveyanceList, loginEmployeeRole)
        : false,
    [hideActionButtons, loginEmployeeRole, shouldUseManagerColumns],
  );

  return (
    <TableWrapper className="table-responsive user-datatable custom-scrollbar mb-2">
      <CustomDataTable
        striped
        noHeader
        selectableRows={
          disableSelection ||
          (loginEmployeeRole === "account" && filterStatus === "approved")
            ? false
            : true
        }
        pageSize={pageSize}
        columns={columns}
        data={tableData}
        onPageChange={onPageChange}
        totalRows={
          useManagerColumns && isAccount
            ? tableData?.length
            : data?.pagination?.total ?? 0
        }
        onRowsPerPageChange={onRowsPageChange}
        loading={conveyanceReportLoading}
        loaderComponent={<SkeletonTable rows={10} columns={columns} />}
        noDataComponent={
          <P className="text-center py-3">No conveyance data found.</P>
        }
        customStyles={{
          headCells: {
            style: {
              paddingLeft:
                loginEmployeeRole === "account" && filterStatus === "approved"
                  ? 10
                  : 0,
              paddingRight:
                loginEmployeeRole === "account" && filterStatus === "approved"
                  ? 10
                  : 0,
            },
          },
        }}
        onSelectRowsChange={handleSelectRowsChange}
        selectableRowDisabled={isRowSelectionDisabled}
        toggleCleared={isClearSelectedRows}
      />
    </TableWrapper>
  );
};

export default memo(TeamMembersConveyanceTable);
